import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared/DashKit";
import { useContent } from "@/context/ContentContext";
import { Loader2, Plus, Send, ArrowLeft, MessageSquarePlus, Package, Star, CheckCheck } from "lucide-react";
import { STATUS_META, metaLabel } from "@/lib/supportUi";
import { AttachmentPicker, AttachmentList, uploadTicketFiles } from "@/components/shared/SupportAttachments";
import { MsgTicks, newClientMsgId, sendWithRetryId } from "@/components/support/MsgTicks";
import { useTr } from "@/lib/tr";

const SERVE = "/support/attachments";

function NewTicket({ orders, onCreated, onCancel }) {
  const { t } = useContent();
  const [f, setF] = useState({ subject: "", message: "", order_id: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.subject.trim() || !f.message.trim()) return toast.error(t("client.sup.subject_msg_required", "Sujet et message requis"));
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", { ...f, order_id: f.order_id || null });
      if (files.length) {
        try { await uploadTicketFiles(files, `/support/tickets/${data.id}/attachments`, data.message_id); }
        catch { toast.error(t("client.sup.att_error", "Certaines pièces jointes n'ont pas pu être envoyées")); }
      }
      toast.success(t("client.sup.created", "Ticket créé"));
      onCreated(data.id);
    } catch (e2) { toast.error(e2.response?.data?.detail || t("client.sup.error", "Erreur")); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="new-ticket-form" className="space-y-3 rounded-2xl border border-border bg-card p-5">
      <input className="w-full rounded-lg border border-border bg-secondary/40 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent/40" placeholder={t("client.sup.subject", "Sujet")} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="ticket-subject" />
      <textarea rows={5} className="w-full rounded-lg border border-border bg-secondary/40 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent/40" placeholder={t("client.sup.describe", "Décrivez votre demande… (français, anglais ou swahili)")} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="ticket-message" />
      {orders.length > 0 && (
        <select className="w-full rounded-lg border border-border bg-secondary/40 px-3 py-2.5 text-sm outline-none" value={f.order_id} onChange={(e) => setF({ ...f, order_id: e.target.value })} data-testid="ticket-order">
          <option value="">{t("client.sup.no_order", "Aucune commande associée")}</option>
          {orders.map((o) => <option key={o.id} value={o.id}>#{o.order_number} — {o.service_name}</option>)}
        </select>
      )}
      <AttachmentPicker files={files} setFiles={setFiles} disabled={busy} idPrefix="new-att" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-full border border-border px-4 py-2 text-sm">{t("client.sup.cancel", "Annuler")}</button>
        <button type="submit" disabled={busy} data-testid="ticket-submit" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("client.sup.send", "Envoyer")}</button>
      </div>
    </form>
  );
}

function CsatWidget({ ticketId, existing, tr, onDone }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(existing?.comment || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!rating) return toast.error(tr("client.sup.csat_pick", "Choisissez une note"));
    setBusy(true);
    try { await api.post(`/support/tickets/${ticketId}/csat`, { rating, comment }); toast.success(tr("client.sup.csat_thanks", "Merci pour votre retour !")); onDone && onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || tr("client.sup.error", "Erreur")); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-border bg-secondary/30 p-4" data-testid="csat-widget">
      <div className="mb-2 text-sm font-medium">{existing ? tr("client.sup.csat_edit", "Votre évaluation") : tr("client.sup.csat_ask", "Comment évaluez-vous notre support ?")}</div>
      <div className="mb-2 flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} data-testid={`csat-star-${n}`}>
            <Star className={`h-6 w-6 ${(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
          </button>
        ))}
      </div>
      <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={tr("client.sup.csat_comment", "Commentaire (facultatif)")} data-testid="csat-comment" className="mb-2 w-full rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm outline-none" />
      <button onClick={submit} disabled={busy} data-testid="csat-submit" className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {tr("client.sup.csat_send", "Envoyer mon avis")}</button>
    </div>
  );
}

function Thread({ id, onBack }) {
  const { t: tr } = useContent();
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const [pending, setPending] = useState([]);
  const tr2 = useTr();
  const lastTyped = useRef(0);
  const load = () => api.get(`/support/tickets/${id}`).then((r) => setT(r.data));
  useEffect(() => { load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); }, [id]);
  useEffect(() => {
    const iv = setInterval(() => api.get(`/support/tickets/${id}/typing`).then((r) => setTyping(!!r.data.typing)).catch(() => {}), 3000);
    return () => clearInterval(iv);
  }, [id]);
  const onType = () => { const now = Date.now(); if (now - lastTyped.current > 2500) { lastTyped.current = now; api.post(`/support/tickets/${id}/typing`, {}).catch(() => {}); } };
  const post = async (p) => {
    const { data } = await api.post(`/support/tickets/${id}/reply`, { message: p.message, client_msg_id: p.cid });
    if (p.files?.length) {
      try { await uploadTicketFiles(p.files, `/support/tickets/${id}/attachments`, data.message_id); }
      catch { toast.error(tr("client.sup.att_error", "Certaines pièces jointes n'ont pas pu être envoyées")); }
    }
    await load();
  };
  const send = async () => {
    if (!msg.trim() && !files.length) return;
    const p = { cid: newClientMsgId(), message: msg.trim() || "(pièce jointe)", files, created_at: new Date().toISOString() };
    setMsg(""); setFiles([]);
    setBusy(true);
    try { await sendWithRetryId(post, p, setPending); } catch { /* shown as failed with retry */ } finally { setBusy(false); }
  };
  const retry = (p) => sendWithRetryId(post, p, setPending).catch(() => {});
  if (!t) return <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>;
  const isClosed = t.status === "closed";
  const isResolved = t.status === "resolved";
  return (
    <div className="rounded-2xl border border-border bg-card" data-testid="ticket-thread">
      <div className="flex items-center gap-3 border-b border-border p-4">
        <button onClick={onBack} data-testid="thread-back"><ArrowLeft className="h-5 w-5 text-muted-foreground" /></button>
        <div className="min-w-0"><div className="truncate font-display font-bold">{t.subject}</div>
          <span className={`rounded px-2 py-0.5 text-[10px] ${STATUS_META[t.status]?.color}`}>{metaLabel(STATUS_META[t.status], tr)}</span>
          {t.agent ? (
            <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="thread-agent">
              <span className={`h-2 w-2 rounded-full ${t.agent.online ? "bg-emerald-500" : "bg-slate-400"}`} />{t.agent.first_name} · {tr2("Support TWP", "TWP Support")}
            </span>
          ) : <span className="ml-2 text-[11px] text-muted-foreground" data-testid="thread-waiting-agent">{tr2("En attente d'un agent", "Waiting for an agent")}</span>}</div>
      </div>
      {t.order && (
        <div className="border-b border-border bg-secondary/30 p-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 font-medium text-foreground"><Package className="h-3.5 w-3.5" /> {tr("client.sup.order_num", "Commande #")}{t.order.order_number}</span> · {t.order.service_name} · {t.order.progress}%
        </div>
      )}
      <div className="max-h-[50vh] space-y-3 overflow-y-auto p-4">
        {[...(t.messages || []), ...pending.filter((p) => !(t.messages || []).some((m) => m.client_msg_id === p.cid)).map((p) => ({ ...p, id: p.cid, sender_role: "customer" }))].map((m) => (
          <div key={m.id} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.sender_role === "customer" ? "ml-auto bg-accent text-white" : "bg-secondary text-foreground"}`}>
            <div className="mb-0.5 text-[10px] opacity-70">{m.sender_role === "customer" ? tr("client.sup.you", "Vous") : `${m.sender_name || "Support"} · ${tr2("Support TWP", "TWP Support")}`} · {new Date(m.created_at).toLocaleString(tr2.lang === "en" ? "en-GB" : "fr-FR")}</div>
            {m.message}
            <AttachmentList attachments={m.attachments} serveBase={SERVE} />
            {m.sender_role === "customer" && <MsgTicks m={m} tr={tr2} onRetry={retry} />}
          </div>
        ))}
        {typing && <div className="text-xs italic text-muted-foreground" data-testid="typing-indicator">{tr("client.sup.support_typing", "Le support est en train d'écrire…")}</div>}
      </div>
      {(isResolved || isClosed) && <CsatWidget ticketId={id} existing={t.csat} tr={tr} onDone={load} />}
      {(isResolved || isClosed) && (
        <div className="border-t border-border bg-secondary/30 px-3 pt-2 text-center text-xs text-muted-foreground" data-testid="reopen-hint">
          {tr("client.sup.reopen_hint", "Répondez ci-dessous pour rouvrir ce ticket.")}
        </div>
      )}
      <div className="space-y-2 border-t border-border p-3">
        <div className="flex gap-2">
          <input value={msg} onChange={(e) => { setMsg(e.target.value); onType(); }} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={tr("client.sup.your_message", "Votre message…")} data-testid="thread-reply-input" className="w-full rounded-full border border-border bg-secondary/40 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-accent/40" />
          <button onClick={send} disabled={busy} data-testid="thread-reply-send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
        </div>
        <AttachmentPicker files={files} setFiles={setFiles} disabled={busy} idPrefix="thread-att" />
      </div>
    </div>
  );
}

export default function Support() {
  const { t } = useContent();
  const [view, setView] = useState("list");
  const [tickets, setTickets] = useState(null);
  const [orders, setOrders] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
  useEffect(() => { load(); api.get("/orders?limit=100").then((r) => setOrders(r.data || [])).catch(() => {}); }, []);

  return (
    <div data-testid="support-page">
      <PageHeader title={t("support.page.title", "Support client")} subtitle={t("support.page.subtitle", "Créez un ticket et suivez vos échanges avec notre équipe.")} />
      <div className="mx-auto max-w-2xl">
        {view === "list" && (
          <>
            <div className="mb-3 flex justify-end">
              <button onClick={() => setView("new")} data-testid="new-ticket-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"><Plus className="h-4 w-4" /> {t("client.sup.new_ticket", "Nouveau ticket")}</button>
            </div>
            {tickets === null ? <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div> : (
              tickets.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                  <MessageSquarePlus className="mx-auto mb-2 h-8 w-8 opacity-50" /> {t("client.sup.empty", "Aucun ticket. Créez-en un pour contacter le support.")}
                </div>
              ) : (
                <div className="divide-y divide-border rounded-2xl border border-border bg-card">
                  {tickets.map((tk) => (
                    <button key={tk.id} onClick={() => { setActiveId(tk.id); setView("thread"); }} data-testid={`my-ticket-${tk.id}`} className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-secondary/40">
                      <div className="min-w-0"><div className="truncate text-sm font-medium">{tk.subject}</div><div className="text-xs text-muted-foreground">{new Date(tk.last_activity_at).toLocaleDateString("fr-FR")}</div></div>
                      <span className={`shrink-0 rounded px-2 py-0.5 text-[10px] ${STATUS_META[tk.status]?.color}`}>{metaLabel(STATUS_META[tk.status], t)}</span>
                    </button>
                  ))}
                </div>
              )
            )}
          </>
        )}
        {view === "new" && <NewTicket orders={orders} onCreated={(id) => { setActiveId(id); setView("thread"); load(); }} onCancel={() => setView("list")} />}
        {view === "thread" && activeId && <Thread id={activeId} onBack={() => { setView("list"); load(); }} />}
      </div>
    </div>
  );
}
